/**
 * Reusable chart components for Dashboard.
 * Uses Recharts. Tailwind for layout.
 */
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';

// Brand-aligned palette (gold + dark navy + accent reds/greens)
const PALETTE = ['#d4a84b', '#1e3a5f', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f59e0b', '#ec4899'];

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  fontSize: '12px',
};
const axisStyle = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };

export function CashFlowTrendChart({ data, formatCurrency, onBarClick }) {
  const chartData = (data || []).map(d => ({
    name: d.month_name,
    label: d.label,
    Receipts: d.receipts,
    Payments: d.payments,
    Net: d.net_flow,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart
        data={chartData}
        margin={{ top: 5, right: 5, left: -10, bottom: 0 }}
        onClick={(e) => { const p = chartData[Number(e?.activeIndex)]; if (p && onBarClick) onBarClick(p, e.activeLabel); }}
      >
        <defs>
          <linearGradient id="colorRecv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity={0.6} />
            <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorPay" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity={0.6} />
            <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis dataKey="name" tick={axisStyle} />
        <YAxis tick={axisStyle} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Area type="monotone" dataKey="Receipts" stroke="#10b981" strokeWidth={2} fill="url(#colorRecv)" style={{ cursor: onBarClick ? 'pointer' : 'default' }} />
        <Area type="monotone" dataKey="Payments" stroke="#ef4444" strokeWidth={2} fill="url(#colorPay)" style={{ cursor: onBarClick ? 'pointer' : 'default' }} />
        <Line type="monotone" dataKey="Net" stroke="#d4a84b" strokeWidth={2} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function PnLTrendChart({ data, formatCurrency, onBarClick }) {
  const chartData = (data || []).map(d => ({
    name: d.month_name, label: d.label, Revenue: d.revenue, Expenses: d.expenses, 'Net Profit': d.net_profit,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis dataKey="name" tick={axisStyle} />
        <YAxis tick={axisStyle} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar
          dataKey="Revenue" fill="#10b981" radius={[4, 4, 0, 0]}
          style={{ cursor: onBarClick ? 'pointer' : 'default' }}
          onClick={(p) => onBarClick && onBarClick(p?.payload || p, null, 'Revenue')}
        />
        <Bar
          dataKey="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]}
          style={{ cursor: onBarClick ? 'pointer' : 'default' }}
          onClick={(p) => onBarClick && onBarClick(p?.payload || p, null, 'Expenses')}
        />
        <Line type="monotone" dataKey="Net Profit" stroke="#d4a84b" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function QuotesInvoicesTrendChart({ data, onPointClick }) {
  const chartData = (data || []).map(d => ({
    name: d.month_name, label: d.label, Quotes: d.quotes_issued, Invoices: d.invoices_issued,
  }));
  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 5, left: -20, bottom: 0 }}
        onClick={(e) => { const p = chartData[Number(e?.activeIndex)]; if (p && onPointClick) onPointClick(p, e.activeLabel, e?.activeDataKey === 'Quotes' || (!p.Invoices && p.Quotes) ? 'Quotes' : 'Invoices'); }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis dataKey="name" tick={axisStyle} />
        <YAxis tick={axisStyle} allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line type="monotone" dataKey="Quotes" stroke="#1e3a5f" strokeWidth={2} dot={(p) => <circle key={`Quotes-${p.index}`} cx={p.cx} cy={p.cy} r={4} fill="#1e3a5f" style={{ cursor: 'pointer' }} onClick={(ev) => { ev.stopPropagation(); onPointClick && onPointClick(p.payload, null, 'Quotes'); }} />} />
        <Line type="monotone" dataKey="Invoices" stroke="#d4a84b" strokeWidth={2} dot={(p) => <circle key={`Invoices-${p.index}`} cx={p.cx} cy={p.cy} r={4} fill="#d4a84b" style={{ cursor: 'pointer' }} onClick={(ev) => { ev.stopPropagation(); onPointClick && onPointClick(p.payload, null, 'Invoices'); }} />} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function SalesPipelineFunnelChart({ buckets, onBarClick }) {
  const order = ['draft', 'pending_approval', 'approved', 'sent', 'client_approved', 'closed'];
  const chartData = order.map(k => ({
    name: k.replace(/_/g, ' '),
    statusKey: k,
    count: buckets?.[k]?.count || 0,
    value: buckets?.[k]?.value || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 5, right: 8, left: 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis type="number" tick={axisStyle} allowDecimals={false} />
        <YAxis dataKey="name" type="category" tick={{ ...axisStyle, fontSize: 10 }} width={100} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar
          dataKey="count"
          fill="#d4a84b"
          radius={[0, 4, 4, 0]}
          style={{ cursor: onBarClick ? 'pointer' : 'default' }}
          onClick={(p) => onBarClick && onBarClick(p?.payload || p)}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function ExpensesPieChart({ data, formatCurrency, onSliceClick }) {
  if (!data || !data.length) return <p className="text-xs text-muted-foreground py-8 text-center">No expenses recorded</p>;
  const chartData = data.map((d) => ({ name: d.account_name, value: d.total, account_id: d.account_id, account_code: d.account_code }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%" cy="50%"
          innerRadius={48} outerRadius={80}
          paddingAngle={2}
          dataKey="value"
          onClick={(slice) => onSliceClick && slice && onSliceClick(slice)}
          style={{ cursor: onSliceClick ? 'pointer' : 'default' }}
        >
          {chartData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
        <Legend wrapperStyle={{ fontSize: 10 }} iconType="circle" />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function AgingBarChart({ buckets, color = '#d4a84b', onBarClick }) {
  const order = [['current','Current'],['1_30','1-30 d'],['31_60','31-60 d'],['61_90','61-90 d'],['90_plus','90+ d']];
  const chartData = order.map(([k, label]) => ({
    name: label, bucketKey: k, count: buckets?.[k]?.count || 0, value: buckets?.[k]?.value || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart
        data={chartData}
        margin={{ top: 5, right: 5, left: -10, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis dataKey="name" tick={axisStyle} />
        <YAxis tick={axisStyle} allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar
          dataKey="count"
          fill={color}
          radius={[4, 4, 0, 0]}
          style={{ cursor: onBarClick ? 'pointer' : 'default' }}
          onClick={(p) => onBarClick && onBarClick(p?.payload || p)}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function TopCustomersBarChart({ data, formatCurrency }) {
  if (!data || !data.length) return <p className="text-xs text-muted-foreground py-8 text-center">No data</p>;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} layout="vertical" margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis type="number" tick={axisStyle} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
        <YAxis dataKey="name" type="category" tick={{ ...axisStyle, fontSize: 10 }} width={100} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
        <Bar dataKey="total" fill="#1e3a5f" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
